import { Box, Collapse, Flex, Text, Paper, Transition } from '@mantine/core';
import { useEffect, useState, useRef } from 'react';
import { IconArrowsMoveVertical } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SelectedFile, STEP_ORDER, StepId } from 'src/suitability-report/types';
import { ReportFormat } from 'src/suitability-report/SuitabilityFlow';

type Props = {
  currentStep: number;
  clientName?: string;
  advisorName?: string;
  templateName?: string;
  reportFormat?: ReportFormat;
  adviceFiles?: SelectedFile[];
  feeDocuments?: SelectedFile[];
  supportDocuments?: SelectedFile[];
  onExpandedChange?: (expanded: boolean, isHoverExpanded: boolean) => void;
  forceExpanded?: boolean;
};

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

const isStepReached = (current: number, target: StepId) => current >= STEP_ORDER.indexOf(target);

export function SiestaChoicesSummary({
  currentStep,
  clientName,
  advisorName,
  templateName,
  adviceFiles = [],
  feeDocuments = [],
  onExpandedChange,
  forceExpanded = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const canShowClient = isStepReached(currentStep, 'advice');
  const canShowAdvice = isStepReached(currentStep, 'documents');
  const canShowFees = isStepReached(currentStep, 'template');
  const canShowTemplate = isStepReached(currentStep, 'support');

  const adviceCount = adviceFiles.length;
  const feeCount = feeDocuments.length;

  const headlineLeft = canShowClient && clientName?.trim() ? clientName : '';
  let headlineMiddle = '';
  if (canShowAdvice) {
    headlineMiddle = adviceCount
      ? `${plural(adviceCount, 'advice doc')} selected`
      : 'No documents selected';
  } else if (canShowFees) {
    headlineMiddle = feeCount ? `${plural(feeCount, 'fee doc')} selected` : 'No documents selected';
  }
  const headline = { left: headlineLeft, middle: headlineMiddle };

  const hasMiddle = !!headline.middle?.trim();
  const showStaticExpanded = forceExpanded || !hasMiddle;

  useEffect(() => {
    onExpandedChange?.(open || showStaticExpanded, open && !showStaticExpanded);
  }, [open, showStaticExpanded, onExpandedChange]);

  const handleEnter = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleLeave = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <Box w={374} pb="lg" style={{ position: 'relative', zIndex: 10 }}>
      {showStaticExpanded ? (
        <>
          {canShowClient && !!clientName && (
            <SummaryRow
              label="Client"
              value={clientName}
              extraLabel={advisorName ? `Advisor: ${advisorName}` : undefined}
            />
          )}
          {canShowAdvice && (
            <SummaryRow
              label="Advice"
              value={
                adviceFiles.length > 0
                  ? adviceFiles.map(x => x.name).join(', ')
                  : 'No documents selected'
              }
            />
          )}
          {canShowFees && (
            <SummaryRow
              label="Fee documents"
              value={
                feeDocuments.length > 0
                  ? feeDocuments.map(x => x.name).join(', ')
                  : 'No documents selected'
              }
            />
          )}
          {canShowTemplate && !!templateName && (
            <SummaryRow label="Template" value={templateName} />
          )}
        </>
      ) : (
        <Box onMouseEnter={handleEnter} onMouseLeave={handleLeave} w="100%">
          <Transition mounted={!open} duration={180} transition="fade-up" timingFunction="ease">
            {styles => (
              <div style={styles}>
                <Paper
                  radius={14}
                  p="xxs"
                  style={{
                    borderRadius: 24,
                    background: 'rgba(201, 74, 44, 1)',
                    backdropFilter: 'blur(48px)',
                    border: '1px solid rgba(253,253,253,0.15)',
                  }}
                >
                  <Flex align="center" gap="sm" w="100%" px="md" py="xxxs">
                    <Text truncate size="xs" fw={FONT_WEIGHTS.bold} c="white">
                      {headline.left}
                    </Text>

                    <Text
                      size="xs"
                      fw={FONT_WEIGHTS.medium}
                      c="white"
                      flex={1}
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        WebkitMaskImage:
                          'linear-gradient(to right, black 0%, black 70%, transparent 100%)',
                        maskImage:
                          'linear-gradient(to right, black 0%, black 70%, transparent 100%)',
                      }}
                    >
                      {headline.middle}
                    </Text>

                    <IconArrowsMoveVertical size={16} color="white" style={{ flexShrink: 0 }} />
                  </Flex>
                </Paper>
              </div>
            )}
          </Transition>

          <Transition mounted={open} duration={180} transition="fade-down" timingFunction="ease">
            {styles => (
              <div style={styles}>
                <Collapse in={open} transitionDuration={220} transitionTimingFunction="ease">
                  {canShowClient && !!clientName && (
                    <SummaryRow
                      label="Client"
                      value={clientName}
                      extraLabel={advisorName ? `Advisor: ${advisorName}` : undefined}
                    />
                  )}
                  {canShowAdvice && (
                    <SummaryRow
                      label="Advice"
                      value={
                        adviceFiles.length > 0
                          ? adviceFiles.map(x => x.name).join(', ')
                          : 'No documents selected'
                      }
                    />
                  )}
                  {canShowFees && (
                    <SummaryRow
                      label="Fee documents"
                      value={
                        feeDocuments.length > 0
                          ? feeDocuments.map(x => x.name).join(', ')
                          : 'No documents selected'
                      }
                    />
                  )}
                  {canShowTemplate && !!templateName && (
                    <SummaryRow label="Template" value={templateName} />
                  )}
                </Collapse>
              </div>
            )}
          </Transition>
        </Box>
      )}
    </Box>
  );
}

function SummaryRow({
  label,
  value,
  extraLabel,
}: {
  label: string;
  value?: string | null;
  extraLabel?: string | null;
}) {
  return (
    <Paper
      radius={14}
      p="xxs"
      mb="xxxs"
      style={{
        borderRadius: 24,
        background: 'rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(48px)',
        border: '1px solid rgba(253,253,253,0.15)',
      }}
    >
      <Flex align="center" justify="space-between" px="md" pt="xs">
        <Text size="xs" fw={FONT_WEIGHTS.medium} c="white">
          {label}
        </Text>
        {extraLabel ? (
          <Text size="xs" fw={FONT_WEIGHTS.medium} c="white" opacity="50%">
            {extraLabel}
          </Text>
        ) : null}
      </Flex>
      <Text size="md" fw={FONT_WEIGHTS.bold} c="white" px="md" pb="xs">
        {value}
      </Text>
    </Paper>
  );
}
